@file:Suppress("TooManyFunctions")

package top.wcpe.allincore.ui.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.WheelEvent
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.CharEvent
import top.wcpe.allincore.ui.runtime.event.DragSession
import top.wcpe.allincore.ui.runtime.event.FocusManager
import top.wcpe.allincore.ui.runtime.event.InputDispatcher
import top.wcpe.allincore.ui.runtime.event.InputTraceEntry
import top.wcpe.allincore.ui.runtime.event.InputTraceKind
import top.wcpe.allincore.ui.runtime.event.InputTraceSink
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.SessionHitRegistry
import top.wcpe.allincore.ui.runtime.event.TargetPathResolver
import top.wcpe.allincore.ui.web.input.DeduplicatingWebUnknownKeyDiagnosticSink
import top.wcpe.allincore.ui.web.input.DomCodeUiKeyMapper
import top.wcpe.allincore.ui.web.input.WebKeyAction
import top.wcpe.allincore.ui.web.input.WebKeyHoldTracker
import top.wcpe.allincore.ui.web.input.WebModifiers
import top.wcpe.allincore.ui.web.input.WebTextInputStateMachine
import top.wcpe.allincore.ui.web.input.WebUnknownKeyDiagnostic
import kotlin.math.hypot

private const val ACTION_RELEASE = 0
private const val ACTION_PRESS = 1
private const val ACTION_REPEAT = 2
private const val WHEEL_NORMALIZE = -100.0
private const val DOUBLE_CLICK_MS = 500L
private const val DOUBLE_CLICK_DISTANCE = 4.0

private fun warnUnknownKey(diagnostic: WebUnknownKeyDiagnostic) {
    console.warn(
        "未知 DOM 物理键：code=${diagnostic.code} domCode=${diagnostic.domCode} " +
            "location=${diagnostic.location} action=${diagnostic.action} target=${diagnostic.targetPath} " +
            "mappingVersion=${diagnostic.mappingVersion}",
    )
}

internal interface WebInputLifecycle {
    fun install()
    fun uninstall()
}

/** FR-UI-066 可注册的独占命中处理接缝；DOM listener 仍只由 [WebInputAdapter] 持有。 */
internal interface WebInputOwnerHandler {
    fun pointerDown(x: Double, y: Double, button: Int): Boolean = false
    fun pointerMove(x: Double, y: Double, dx: Double, dy: Double): Boolean = false
    fun pointerUp(x: Double, y: Double, button: Int): Boolean = false
    fun pointerCancel(): Boolean = false
    fun scroll(x: Double, y: Double, sx: Double, sy: Double): Boolean = false
}

internal class WebInputAdapter(
    private val canvas: HTMLCanvasElement,
    private val rootProvider: () -> UIElement<*>?,
    private val layoutProvider: () -> LayoutResult?,
    private val rerender: () -> Unit,
    private val scaleProvider: () -> Double = { 1.0 },
    private val focusManagerProvider: () -> FocusManager? = { null },
    private val sessionIdProvider: () -> String = { "web-preview" },
    private val hitRegistryProvider: () -> SessionHitRegistry<WebInputOwnerHandler>? = { null },
    private val traceSinkProvider: () -> InputTraceSink? = { null },
    private val diagnosticConsumer: (WebUnknownKeyDiagnostic) -> Unit = ::warnUnknownKey,
    private val nowProvider: () -> Long = { window.performance.now().toLong() },
) : WebInputLifecycle {
    private val fallbackFocusManager = FocusManager()
    private val keyHoldTracker = WebKeyHoldTracker()
    private var unknownKeySink = DeduplicatingWebUnknownKeyDiagnosticSink(diagnosticConsumer)
    private val listeners = ArrayList<ListenerBinding>()
    private val textMachine = WebTextInputStateMachine(::dispatchCodePoint)
    private var hiddenInput: HTMLInputElement? = null
    private var capture: PointerCapture? = null
    private var clickCandidate: ClickCandidate? = null
    private var installed = false
    private var renderFrameId = -1
    private var currentModifiers = 0

    override fun install() {
        if (installed) return
        installed = true
        hiddenInput = createHiddenInput()
        bind(canvas, "pointerdown", ::onPointerDown)
        bind(canvas, "pointermove", ::onPointerMove)
        bind(canvas, "pointerup", ::onPointerUp)
        bind(canvas, "pointercancel", ::onPointerCancel)
        bind(canvas, "wheel", ::onWheel)
        bind(document, "keydown", ::onKeyDown)
        bind(document, "keyup", ::onKeyUp)
        bind(window, "blur", ::onWindowBlur)
        bindTextInput(hiddenInput!!)
    }

    override fun uninstall() {
        if (!installed) return
        installed = false
        resetSession()
        listeners.forEach { it.target.removeEventListener(it.type, it.listener) }
        listeners.clear()
        hiddenInput?.remove()
        hiddenInput = null
        if (renderFrameId >= 0) window.cancelAnimationFrame(renderFrameId)
        renderFrameId = -1
    }

    fun resetSession() {
        cancelInput(clearFocus = true)
        hitRegistryProvider()?.clear()
        unknownKeySink = DeduplicatingWebUnknownKeyDiagnosticSink(diagnosticConsumer)
    }

    private fun onPointerDown(event: Event) {
        if (!installed) return
        val dom = event.asDynamic()
        if (dom.isPrimary == false) return
        val point = logicalPoint(dom)
        val button = normalizePointerButton(dom.button as Int)
        val root = rootProvider() ?: return
        val layout = layoutProvider() ?: return
        val target = InputDispatcher.hitElement(root, layout, point.x, point.y)
        val targetPath = TargetPathResolver.resolve(root, target)
        val owner = hitRegistryProvider()?.ownerAt(point.x, point.y)
        val focus = focusManager()
        val focusBefore = targetPath(root, focus.current())
        focus.dispatchClickFocus(root, layout, point.x, point.y)
        val focusAfter = targetPath(root, focus.current())
        syncHiddenInputFocus(focus.current())
        setDomPointerCapture(dom.pointerId as Int)
        val active = PointerCapture(dom.pointerId as Int, button, point, point, targetPath, owner, nowProvider())
        capture = active
        val ownerResult = owner?.let { callOwner("按下") { it.pointerDown(point.x, point.y, button) } }
        val handled = ownerResult?.handled ?: InputDispatcher.dispatchClick(root, layout, point.x, point.y, button)
        tracePointer(InputTraceKind.POINTER_DOWN, targetPath, point, button, handled, focusBefore, focusAfter)
        traceFocus(focusBefore, focusAfter)
        if (ownerResult?.failed == true) {
            tracePointer(InputTraceKind.CANCEL, targetPath, point, button, false)
            cancelPointer()
            requestRender()
            return
        }
        tracePointer(InputTraceKind.CLICK, targetPath, point, button, handled, focusBefore, focusAfter)
        active.doubleClicked = if (owner == null) {
            maybeDispatchDoubleClick(root, layout, point, button, targetPath, handled)
        } else {
            false
        }
        requestRender()
    }

    private fun onPointerMove(event: Event) {
        if (!installed) return
        val dom = event.asDynamic()
        if (dom.isPrimary == false) return
        val point = logicalPoint(dom)
        val root = rootProvider() ?: return
        val layout = layoutProvider() ?: return
        val active = capture?.takeIf { it.pointerId == dom.pointerId as Int }
        if (active?.owner != null) {
            dispatchCapturedMove(root, layout, active, point)
            requestRender()
            return
        }
        InputDispatcher.dispatchMouseMove(root, layout, point.x, point.y)
        val targetPath = TargetPathResolver.resolve(root, InputDispatcher.hitElement(root, layout, point.x, point.y))
        tracePointer(InputTraceKind.MOVE, targetPath, point, null, false)
        if (active != null) dispatchCapturedMove(root, layout, active, point)
        requestRender()
    }

    private fun dispatchCapturedMove(
        root: UIElement<*>,
        layout: LayoutResult,
        active: PointerCapture,
        point: LogicalPoint,
    ) {
        val dx = point.x - active.last.x
        val dy = point.y - active.last.y
        active.last = point
        if (hypot(point.x - active.start.x, point.y - active.start.y) > DOUBLE_CLICK_DISTANCE) active.dragged = true
        val handled = active.owner?.let {
            callOwner("移动") { it.pointerMove(point.x, point.y, dx, dy) }.handled
        } ?: InputDispatcher.dispatchDrag(root, layout, point.x, point.y, active.button, dx, dy)
        if (active.owner == null) InputDispatcher.dispatchDragMove(root, layout, point.x, point.y)
        traceSinkProvider()?.record(
            InputTraceEntry.nonKey(
                InputTraceKind.DRAG,
                active.targetPath,
                handled,
                point.x,
                point.y,
                dx,
                dy,
                active.button,
                buttonName(active.button),
            ),
        )
    }

    private fun onPointerUp(event: Event) {
        if (!installed) return
        val dom = event.asDynamic()
        val active = capture?.takeIf { it.pointerId == dom.pointerId as Int } ?: return
        val point = logicalPoint(dom)
        val root = rootProvider()
        val layout = layoutProvider()
        val owner = active.owner
        val dropHandled = if (owner != null) {
            callOwner("抬起") { owner.pointerUp(point.x, point.y, active.button) }.handled
        } else {
            InputDispatcher.dispatchDrop(root, layout, point.x, point.y)
        }
        InputDispatcher.releaseDragCapture()
        releaseDomPointerCapture(active.pointerId)
        capture = null
        if (active.owner != null || active.dragged || active.doubleClicked) {
            clickCandidate = null
        } else {
            rememberClickCandidate(active, point)
        }
        tracePointer(InputTraceKind.UP, targetPathAt(root, layout, point), point, active.button, dropHandled)
        requestRender()
    }

    private fun onPointerCancel(event: Event) {
        if (!installed) return
        val dom = event.asDynamic()
        val active = capture?.takeIf { it.pointerId == dom.pointerId as Int } ?: return
        val point = logicalPoint(dom)
        tracePointer(InputTraceKind.CANCEL, active.targetPath, point, active.button, false)
        cancelPointer()
        requestRender()
    }

    private fun onWheel(event: Event) {
        if (!installed) return
        val dom = event.unsafeCast<WheelEvent>()
        val point = logicalPoint(event.asDynamic())
        val sx = dom.deltaX / WHEEL_NORMALIZE
        val sy = dom.deltaY / WHEEL_NORMALIZE
        val owner = hitRegistryProvider()?.ownerAt(point.x, point.y)
        val handled = owner?.let {
            callOwner("滚动") { it.scroll(point.x, point.y, sx, sy) }.handled
        } ?: InputDispatcher.dispatchScrollConsumed(rootProvider(), layoutProvider(), point.x, point.y, sx, sy)
        if (handled) event.preventDefault()
        traceSinkProvider()?.record(
            InputTraceEntry.nonKey(
                InputTraceKind.SCROLL,
                targetPathAt(rootProvider(), layoutProvider(), point),
                handled,
                point.x,
                point.y,
                scrollX = sx,
                scrollY = sy,
            ),
        )
        requestRender()
    }

    private fun onKeyDown(event: Event) = dispatchKey(event.unsafeCast<KeyboardEvent>(), release = false)

    private fun onKeyUp(event: Event) = dispatchKey(event.unsafeCast<KeyboardEvent>(), release = true)

    private fun dispatchKey(dom: KeyboardEvent, release: Boolean) {
        if (!installed) return
        val code = dom.code
        val timing = if (release) {
            keyHoldTracker.keyUp(code, nowProvider())
        } else {
            keyHoldTracker.keyDown(code, nowProvider(), dom.repeat)
        }
        val action = when (timing.action) {
            WebKeyAction.RELEASE -> ACTION_RELEASE
            WebKeyAction.PRESS -> ACTION_PRESS
            WebKeyAction.REPEAT -> ACTION_REPEAT
        }
        val modifiers = modifiers(dom)
        currentModifiers = modifiers
        val key = DomCodeUiKeyMapper.map(code, dom.key, dom.isComposing)
        val focus = focusManager()
        val focused = focus.current()
        val root = rootProvider()
        val focusedPath = targetPath(root, focused)
        if (key == DomCodeUiKeyMapper.UNKNOWN) {
            unknownKeySink.emit(
                sessionIdProvider(),
                code,
                dom.key,
                dom.location,
                timing.action,
                modifiers,
                focusedPath,
            )
        }
        val event = KeyEvent(
            key,
            DomCodeUiKeyMapper.SCAN_CODE,
            modifiers,
            action,
            DomCodeUiKeyMapper.VANILLA_KEY,
            timing.holdMs,
        )
        val result = InputDispatcher.dispatchKeyResult(root, focused, event)
        var consumed = result.consumed
        if (!release && !consumed && key == "Tab") {
            if ((modifiers and 1) != 0) focus.cyclePrev(root) else focus.cycleNext(root)
            consumed = focus.current() != focused
            syncHiddenInputFocus(focus.current())
            traceFocus(focusedPath, targetPath(root, focus.current()))
        }
        if (!release && !consumed && key == "Enter" && focused !is TextField) {
            val activate = focused?.onClickHandler()
            if (activate != null) {
                try {
                    activate.accept(MouseEvent(0.0, 0.0, 0))
                } catch (_: Throwable) {
                    console.error("WEB_INPUT_ACTIVATE_FAILED：Enter 激活处理器执行失败，已隔离本次异常")
                }
                consumed = true
            }
        }
        if (consumed) dom.preventDefault()
        traceSinkProvider()?.record(
            InputTraceEntry.key(
                focusedPath,
                key,
                action,
                modifiers,
                timing.holdMs,
                result.copy(handled = result.handled || consumed, consumed = consumed),
                DomCodeUiKeyMapper.MAPPING_VERSION,
            ),
        )
        requestRender()
    }

    private fun bindTextInput(input: HTMLInputElement) {
        bind(input, "beforeinput") { event ->
            val dom = event.asDynamic()
            textMachine.beforeInput(
                dom.data as String?,
                dom.inputType as? String ?: "",
                dom.isComposing == true,
                hasTextFocus(),
            )
        }
        bind(input, "input") { event ->
            val dom = event.asDynamic()
            textMachine.input(
                dom.data as String?,
                dom.inputType as? String ?: "",
                dom.isComposing == true,
                hasTextFocus(),
            )
            input.value = ""
        }
        bind(input, "compositionstart") { textMachine.compositionStart(hasTextFocus()) }
        bind(input, "compositionend") { event ->
            textMachine.compositionEnd(event.asDynamic().data as String?, hasTextFocus())
            input.value = ""
        }
        bind(input, "paste") { event ->
            val clipboard = event.asDynamic().clipboardData
            val text = clipboard?.getData("text/plain") as? String
            textMachine.paste(text, hasTextFocus())
            if (hasTextFocus()) event.preventDefault()
            input.value = ""
        }
        bind(input, "blur") { textMachine.clear() }
    }

    private fun dispatchCodePoint(codepoint: Int) {
        val root = rootProvider()
        val focus = focusManager().current()
        val targetPath = targetPath(root, focus)
        val handled = if (focus is TextField) {
            InputDispatcher.dispatchChar(focus, CharEvent(codepoint, currentModifiers))
        } else {
            false
        }
        traceSinkProvider()?.record(
            InputTraceEntry.nonKey(InputTraceKind.CHAR, targetPath, handled, codepoint = codepoint),
        )
        if (handled) requestRender()
    }

    private fun onWindowBlur(@Suppress("UNUSED_PARAMETER") event: Event) {
        if (!installed) return
        cancelInput(clearFocus = true)
        requestRender()
    }

    private fun cancelInput(clearFocus: Boolean) {
        capture?.let { active ->
            tracePointer(InputTraceKind.CANCEL, active.targetPath, active.last, active.button, false)
        }
        cancelPointer()
        keyHoldTracker.clear()
        textMachine.clear()
        currentModifiers = 0
        if (clearFocus) {
            val root = rootProvider()
            val focus = focusManager()
            val focusBefore = targetPath(root, focus.current())
            focus.clear()
            traceFocus(focusBefore, targetPath(root, focus.current()))
        }
        hiddenInput?.blur()
    }

    private fun callOwner(action: String, callback: () -> Boolean): OwnerCallResult = runCatching(callback).fold(
        onSuccess = { OwnerCallResult(it, false) },
        onFailure = {
            console.warn("Web 输入 owner ${action}回调失败，已隔离异常并继续清理", it)
            OwnerCallResult(false, true)
        },
    )

    private fun cancelPointer() {
        val active = capture
        if (active != null) {
            active.owner?.let { callOwner("取消") { it.pointerCancel() } }
            releaseDomPointerCapture(active.pointerId)
        }
        capture = null
        clickCandidate = null
        DragSession.current()?.cancel()
        InputDispatcher.releaseDragCapture()
    }

    private fun maybeDispatchDoubleClick(
        root: UIElement<*>,
        layout: LayoutResult,
        point: LogicalPoint,
        button: Int,
        targetPath: String,
        clickHandled: Boolean,
    ): Boolean {
        val previous = clickCandidate ?: return false
        val matches =
            previous.button == button &&
                previous.targetPath == targetPath &&
                nowProvider() - previous.timeMs <= DOUBLE_CLICK_MS &&
                hypot(point.x - previous.down.x, point.y - previous.down.y) <= DOUBLE_CLICK_DISTANCE
        if (!matches) return false
        val handled = InputDispatcher.dispatchDoubleClick(root, layout, point.x, point.y, button)
        tracePointer(InputTraceKind.DOUBLE_CLICK, targetPath, point, button, handled || clickHandled)
        clickCandidate = null
        return true
    }

    private fun rememberClickCandidate(active: PointerCapture, up: LogicalPoint) {
        clickCandidate = ClickCandidate(active.button, active.targetPath, active.start, up, active.downTimeMs)
    }

    private fun createHiddenInput(): HTMLInputElement {
        val input = document.createElement("input").unsafeCast<HTMLInputElement>()
        input.type = "text"
        input.setAttribute("aria-hidden", "true")
        input.tabIndex = -1
        val style = input.style
        style.position = "fixed"
        style.left = "-10000px"
        style.top = "0"
        style.width = "1px"
        style.height = "1px"
        style.opacity = "0"
        document.body?.appendChild(input)
        return input
    }

    private fun syncHiddenInputFocus(focused: UIElement<*>?) {
        if (focused is TextField) hiddenInput?.focus() else hiddenInput?.blur()
    }

    private fun hasTextFocus(): Boolean = focusManager().current() is TextField

    private fun logicalPoint(event: dynamic): LogicalPoint {
        val rect = canvas.getBoundingClientRect()
        // CSS 盒尺寸优先用 getBoundingClientRect；为 0 时回退 offset/client，避免布局瞬态下除零。
        val cssWidth = sequenceOf(
            rect.width,
            canvas.clientWidth.toDouble(),
            canvas.offsetWidth.toDouble(),
        ).firstOrNull { it > 0.0 } ?: 1.0
        val cssHeight = sequenceOf(
            rect.height,
            canvas.clientHeight.toDouble(),
            canvas.offsetHeight.toDouble(),
        ).firstOrNull { it > 0.0 } ?: 1.0
        // clientX/Y 相对 viewport；必须扣 rect 原点，再按 backing buffer / CSS 比换算到 canvas 像素。
        val clientX = (event.clientX as? Number)?.toDouble() ?: 0.0
        val clientY = (event.clientY as? Number)?.toDouble() ?: 0.0
        val backingX = (clientX - rect.left) * canvas.width / cssWidth
        val backingY = (clientY - rect.top) * canvas.height / cssHeight
        val dprX = canvas.width / cssWidth
        val dprY = canvas.height / cssHeight
        val guiScale = scaleProvider().takeIf { it.isFinite() && it > 0.0 } ?: 1.0
        // 逻辑坐标 = canvas 像素 / DPR / guiScale，与 stage.scale=guiScale 对齐。
        return LogicalPoint(backingX / dprX / guiScale, backingY / dprY / guiScale)
    }

    private fun setDomPointerCapture(pointerId: Int) {
        runCatching { canvas.asDynamic().setPointerCapture(pointerId) }
    }

    private fun releaseDomPointerCapture(pointerId: Int) {
        runCatching {
            val dom = canvas.asDynamic()
            if (dom.hasPointerCapture(pointerId) == true) dom.releasePointerCapture(pointerId)
        }
    }

    private fun bind(target: dynamic, type: String, listener: (Event) -> Unit) {
        target.addEventListener(type, listener)
        listeners.add(ListenerBinding(target, type, listener))
    }

    private fun requestRender() {
        if (renderFrameId >= 0) return
        renderFrameId = window.requestAnimationFrame {
            renderFrameId = -1
            if (installed) rerender()
        }
    }

    private fun focusManager(): FocusManager = focusManagerProvider() ?: fallbackFocusManager

    private fun tracePointer(
        kind: InputTraceKind,
        targetPath: String,
        point: LogicalPoint,
        button: Int?,
        handled: Boolean,
        focusBefore: String? = null,
        focusAfter: String? = null,
    ) {
        traceSinkProvider()?.record(
            InputTraceEntry.nonKey(
                kind,
                targetPath,
                handled,
                point.x,
                point.y,
                button = button,
                buttonName = button?.let(::buttonName),
                focusBefore = focusBefore,
                focusAfter = focusAfter,
            ),
        )
    }

    private fun traceFocus(focusBefore: String, focusAfter: String) {
        if (focusBefore == focusAfter) return
        val target = focusAfter.ifEmpty { focusBefore }
        traceSinkProvider()?.record(
            InputTraceEntry.nonKey(
                InputTraceKind.FOCUS,
                target,
                handled = true,
                focusBefore = focusBefore,
                focusAfter = focusAfter,
            ),
        )
    }

    private fun targetPath(root: UIElement<*>?, target: UIElement<*>?): String =
        TargetPathResolver.resolve(root, target)

    private fun targetPathAt(root: UIElement<*>?, layout: LayoutResult?, point: LogicalPoint): String {
        if (root == null || layout == null) return ""
        return TargetPathResolver.resolve(root, InputDispatcher.hitElement(root, layout, point.x, point.y))
    }

    private fun modifiers(event: KeyboardEvent): Int = WebModifiers(
        event.shiftKey,
        event.ctrlKey,
        event.altKey,
        event.metaKey,
    ).bits

    private fun normalizePointerButton(button: Int): Int = when (button) {
        0 -> 0
        2 -> 1
        1 -> 2
        else -> button
    }

    private fun buttonName(button: Int): String = when (button) {
        0 -> "Left"
        1 -> "Right"
        2 -> "Middle"
        3 -> "MouseBack"
        4 -> "MouseForward"
        5 -> "MouseButton6"
        6 -> "MouseButton7"
        else -> ""
    }

    private data class LogicalPoint(val x: Double, val y: Double)

    private data class OwnerCallResult(val handled: Boolean, val failed: Boolean)

    private data class PointerCapture(
        val pointerId: Int,
        val button: Int,
        val start: LogicalPoint,
        var last: LogicalPoint,
        val targetPath: String,
        val owner: WebInputOwnerHandler?,
        val downTimeMs: Long,
        var dragged: Boolean = false,
        var doubleClicked: Boolean = false,
    )

    private data class ClickCandidate(
        val button: Int,
        val targetPath: String,
        val down: LogicalPoint,
        val up: LogicalPoint,
        val timeMs: Long,
    )

    private data class ListenerBinding(val target: dynamic, val type: String, val listener: (Event) -> Unit)
}
